import { useEffect, useState } from "react";
import OneSignal from "react-onesignal";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

/**
 * Inicializa OneSignal e identifica o aluno com external_id = student.id.
 * Mostra prompt amigável na primeira visita se o navegador suporta.
 */
export default function OneSignalProvider({ studentId, children }) {
  const [ready, setReady] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    if (!APP_ID) return;
    if (!("Notification" in window)) return;
    if (initializing || ready) return;
    setInitializing(true);
    (async () => {
      try {
        await OneSignal.init({
          appId: APP_ID,
          serviceWorkerPath: "push/onesignal/OneSignalSDKWorker.js",
          serviceWorkerParam: { scope: "/push/onesignal/" },
          autoResubscribe: true,
          allowLocalhostAsSecureOrigin: true,
        });
        setReady(true);
        const asked = localStorage.getItem("push-prompt-seen");
        if (!asked && Notification.permission === "default") setShowPrompt(true);
      } catch (e) {
        // noop — notifications stay disabled, app funciona normalmente
      }
    })();
  }, [initializing, ready]);

  useEffect(() => {
    if (!ready || !studentId) return;
    OneSignal.login(String(studentId)).catch(() => {});
  }, [ready, studentId]);

  const enablePush = async () => {
    try { await OneSignal.Notifications.requestPermission(); } catch (e) { /* noop */ }
    localStorage.setItem("push-prompt-seen", "1");
    setShowPrompt(false);
  };

  const deferPush = () => {
    localStorage.setItem("push-prompt-seen", "1");
    setShowPrompt(false);
  };

  return (
    <>
      {children}
      <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
        <DialogContent data-testid="push-permission-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">🔔 Receba avisos</DialogTitle>
            <DialogDescription className="text-base">
              Ative as notificações para receber avisos de <b>convocação</b> do Vôlei Vida.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={deferPush} data-testid="defer-push">Agora não</Button>
            <Button onClick={enablePush} data-testid="enable-push" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold">
              Ativar notificações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export async function onesignalLogout() {
  try { await OneSignal.logout(); } catch (e) { /* noop */ }
}
